import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Coins, KeyRound, Check, Loader2, Eye, EyeOff, ShieldCheck, Infinity as InfinityIcon } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import PlansPanel from "@/components/billing/PlansPanel";

// STAGE 6 · PHASE 2 — Profile account-management matrix.
// Account tiers viz + read-only usage bars + BYOK FAL_KEY form.
// FAL key persists via the EXISTING encrypted byok-registry (no new backend).

const FAL_KEY_NAME = "FAL_AI_API_KEY";

const TIERS = [
  { key: "lifetime", label: "Lifetime", desc: "Full access — forever", Icon: InfinityIcon, accent: "#FFD028" },
  { key: "subscription", label: "Subscription", desc: "Monthly credit pool", Icon: Crown, accent: "#D4AF37" },
  { key: "casual", label: "Casual", desc: "Pay-as-you-go credits", Icon: Coins, accent: "#38BDF8" },
  { key: "byok", label: "BYOK", desc: "Bring your own key", Icon: KeyRound, accent: "#A78BFA" },
];

const TIER_CAP = { tier_1: 500, tier_2: 5000, tier_3: 50000 };

function Bar({ label, value, max, accent }) {
  const pct = Math.max(0, Math.min(100, max > 0 ? (value / max) * 100 : 0));
  return (
    <div data-testid={`usage-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-white/50">
        <span>{label}</span>
        <span className="text-white/70">{Math.round(value).toLocaleString()} / {max.toLocaleString()}</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full border border-white/10 bg-white/[0.04]">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
    </div>
  );
}

export default function ProfileSettingsMatrix() {
  const { user, wallet } = useSession();
  const [falSet, setFalSet] = useState(false);
  const [falMasked, setFalMasked] = useState(null);
  const [falInput, setFalInput] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPlans, setShowPlans] = useState(false);

  const loadKeys = async () => {
    try {
      const keys = await vaultSecurityApi.byokRegistry();
      const fal = (keys || []).find((k) => k.name === FAL_KEY_NAME);
      setFalSet(!!fal?.set);
      setFalMasked(fal?.masked || null);
    } catch { /* not critical */ }
    finally { setLoading(false); }
  };
  useEffect(() => { loadKeys(); }, []);

  const balance = Number(wallet?.balance ?? user?.meta?.milink ?? 0);
  const cap = TIER_CAP[user?.subscription_tier] || 500;
  const monthlyCap = Number(user?.meta?.monthlyResetCap ?? cap);
  const mTokens = Number(user?.meta?.mTokens ?? 0);

  // Membership is the PRIMARY axis (Lifetime > Subscription > Casual). BYOK is an
  // ADDITIVE capability, not a replacement — a lifetime/founder who also brings a
  // key is "everything", so both badges light up.
  const isFounder = !!(user?.is_founder || user?.founder || user?.role === "founder");
  const isLifetime = !!(user?.lifetime_access || user?.subscription_plan === "lifetime" || user?.subscription_tier === "lifetime" || isFounder);
  const isSub = ["tier_2", "tier_3"].includes(user?.subscription_tier);
  const isByok = falSet;
  const activeSet = {
    lifetime: isLifetime,
    subscription: !isLifetime && isSub,
    casual: !isLifetime && !isSub,
    byok: isByok,
  };
  const primaryLabel = isLifetime ? "Lifetime" : isSub ? "Subscription" : "Casual";

  const saveFal = async () => {
    const v = falInput.trim();
    if (v.length < 4) { toast.error("Enter a valid FAL key"); return; }
    setSaving(true);
    try {
      await vaultSecurityApi.byokRegSet(FAL_KEY_NAME, v);
      setFalInput("");
      await loadKeys();
      toast.success("FAL key saved securely");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save key"); }
    finally { setSaving(false); }
  };

  const wipeFal = async () => {
    setSaving(true);
    try { await vaultSecurityApi.byokRegWipe(FAL_KEY_NAME); await loadKeys(); toast("FAL key removed"); }
    catch { toast.error("Could not remove key"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="profile-settings-matrix">
      {/* Identity strip */}
      <div className="flex items-center gap-3 rounded-2xl border-2 border-white/10 bg-white/[0.03] p-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl border-2 border-gold/50 bg-gold/10">
          <ShieldCheck size={20} className="text-gold" />
        </span>
        <div className="leading-tight">
          <p className="font-sans text-[15px] font-bold text-white">{user?.name || user?.handle || user?.user_id}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">{user?.temporal_id || user?.email}</p>
        </div>
      </div>

      {/* Account Tier Matrix */}
      <div>
        <p className="mb-2 font-mono text-[10px] font-black uppercase tracking-[0.24em] text-white/60">Account Tier</p>
        <div className="grid grid-cols-2 gap-2" data-testid="tier-matrix">
          {TIERS.map((t) => {
            const active = activeSet[t.key];
            const Icon = t.Icon;
            return (
              <div key={t.key} data-testid={`tier-card-${t.key}`}
                className={["relative flex flex-col items-start gap-1.5 rounded-2xl border-2 p-2.5 transition-colors",
                  active ? "bg-white/[0.06]" : "border-white/10 bg-white/[0.02]"].join(" ")}
                style={active ? { borderColor: t.accent } : {}}>
                <span className="grid h-8 w-8 place-items-center rounded-lg border-2" style={{ borderColor: active ? t.accent : "rgba(255,255,255,0.15)" }}>
                  <Icon size={15} style={{ color: active ? t.accent : "rgba(255,255,255,0.4)" }} />
                </span>
                <span className="font-sans text-[12px] font-bold leading-tight text-white">{t.label}</span>
                <span className="font-mono text-[7.5px] uppercase tracking-wide text-white/40">{t.desc}</span>
                {active && (
                  <span className="absolute right-2 top-2 grid h-4 w-4 place-items-center rounded-full" style={{ backgroundColor: t.accent }}>
                    <Check size={11} strokeWidth={3.5} className="text-slate-950" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-1.5 font-mono text-[8px] uppercase tracking-wide text-white/30" data-testid="active-tier-label">
          Current plan · {primaryLabel}{isByok ? " + BYOK" : ""}
        </p>
        <button onClick={() => setShowPlans(true)} data-testid="manage-plan-btn"
          className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-full border-2 border-gold bg-gold/10 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.18em] text-gold active:scale-95">
          <Crown size={13} /> Manage Plan · Buy Credits
        </button>
      </div>

      {/* Usage indicators (read-only) */}
      <div className="space-y-3 rounded-2xl border-2 border-white/10 bg-white/[0.03] p-3.5" data-testid="usage-indicators">
        <p className="font-mono text-[10px] font-black uppercase tracking-[0.24em] text-white/60">Usage</p>
        <Bar label="Credits" value={balance} max={cap} accent="#38BDF8" />
        <Bar label="Monthly Credits" value={Math.min(monthlyCap, balance)} max={monthlyCap} accent="#D4AF37" />
        <Bar label="M-Tokens" value={mTokens} max={Math.max(1000, mTokens)} accent="#A78BFA" />
      </div>

      {/* BYOK FAL key */}
      <div className="space-y-2.5 rounded-2xl border-2 border-violet-400/30 bg-violet-500/[0.06] p-3.5" data-testid="fal-key-panel">
        <div className="flex items-center gap-2">
          <KeyRound size={15} className="text-violet-300" />
          <p className="font-mono text-[10px] font-black uppercase tracking-[0.2em] text-violet-200">Personal FAL Key (BYOK)</p>
        </div>
        {falSet && (
          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-2.5 py-2" data-testid="fal-key-status">
            <span className="font-mono text-[11px] text-emerald-300">{falMasked || "•••• configured"}</span>
            <button onClick={wipeFal} disabled={saving} data-testid="fal-key-wipe" className="font-mono text-[9px] font-black uppercase tracking-widest text-red-300">Remove</button>
          </div>
        )}
        <div className="relative">
          <input
            data-testid="fal-key-input"
            type={show ? "text" : "password"}
            value={falInput}
            onChange={(e) => setFalInput(e.target.value)}
            placeholder={falSet ? "Update FAL key…" : "Paste your fal.ai API key"}
            className="w-full rounded-xl border-2 border-white/15 bg-black/40 px-3 py-2.5 pr-10 font-mono text-[12px] text-white placeholder:text-white/30 outline-none focus:border-violet-400"
          />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid="fal-key-toggle" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button
          onClick={saveFal}
          disabled={saving || loading}
          data-testid="fal-key-save"
          className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-violet-400 bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 font-mono text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-[3px_3px_0px_#312E81] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          {falSet ? "Update FAL Key" : "Save FAL Key"}
        </button>
        <p className="font-mono text-[8px] leading-snug text-white/30">Stored encrypted at rest · used to run your AI pipelines under your own quota.</p>
      </div>

      {showPlans && <PlansPanel onBack={() => setShowPlans(false)} />}
    </div>
  );
}
